<template>
  <b-container>
    <b-row v-if="hasData" align-h="center">
      <e-chart
        :options="chartOptions"
        @click="handleClickEvent"
        auto-resize
      ></e-chart>
    </b-row>
    <b-row v-else-if="loaded" align-h="center">
      <div>{{ $t("No Data Available") }}</div>
    </b-row>
    <b-row v-else align-h="center">
      <SpinnerCmpt></SpinnerCmpt>
    </b-row>
    <b-row v-if="hasData && selectedPoint" align-h="center">
      <IpTrafficPointDetails :selectedPoint="selectedPoint"></IpTrafficPointDetails>
    </b-row>
    <b-row v-else-if="hasData && !selectedPoint" align-h="center">
      <h5>Select a point on the chart to view records per bucket.</h5>
    </b-row>
  </b-container>
</template>

<script>
import moment from 'moment';

import IpTrafficPointDetails from '@/xvisor/components/security/IpTrafficPointDetails.vue';
import SpinnerCmpt from '@/xvisor/components/SpinnerCmpt.vue';
import colorPaletteShade from '@/xvisor/constants/colorPaletteShade';
import colorPalette from '@/xvisor/constants/colorPalette';
import timeFormat from '@/xvisor/utilities/timeFormat';
import readableBytes from '@/xvisor/utilities/readableBytes';
import granularity from '@/xvisor/utilities/granularity';

export default {
  props: {
    timeRange: {
      type: Object,
      required: true,
    },
    role: {
      type: String,
      required: true,
    },
    selectedServer: {
      type: Object,
      required: true,
    },
    serviceType: {
      type: String,
      required: true,
    },
  },
  components: {
    IpTrafficPointDetails,
    SpinnerCmpt,
  },
  data() {
    return {
      tsData: null,
      loaded: false,
      selectedPoint: null,
      markPointData: [],
    };
  },
  mounted() {
    this.httpGet();
  },
  watch: {
    selectedServer: {
      handler() {
        this.httpGet();
      },
      deep: true,
    },
    timeRange() {
      this.httpGet();
    },
  },
  computed: {
    hasData() {
      return this.loaded && this.tsData && this.tsData.length > 0;
    },
    timeSet() {
      return new Set(this.tsData.map((query) => query.time));
    },
    timeRangeMax() {
      const maxDate = new Date(
        Math.max(...this.tsData.map((element) => new Date(element.time))),
      );
      return maxDate;
    },
    timeRangeMin() {
      const minDate = new Date(
        Math.min(
          ...this.tsData.map((element) => new Date(element.time)),
        ),
      );
      return minDate;
    },
    timeSeriesFormat() {
      return timeFormat.momentFormat(this.timeSet, new Date());
    },
    httpUrl() {
      return `/security/${this.selectedServer.ip}/details.json`;
    },
    httpParams() {
      return {
        params: {
          start: this.timeRange.start.toISOString(),
          end: this.timeRange.end.toISOString(),
          stepMinutes: granularity.granularity24(this.timeRange),
          service: this.serviceType,
          role: this.role,
          scope: this.selectedServer.scope,
        },
      };
    },
    getSeriesValues() {
      const trafficData = this.tsData.map((item) => [
        moment(item.time).valueOf(),
        item.value,
      ]);
      return {
        type: 'line',
        lineStyle: {
          color: colorPaletteShade.indigo6,
          width: 5,
        },
        symbol: 'circle',
        symbolSize: 10,
        data: trafficData,
        markPoint: {
          symbol: 'triangle',
          symbolSize: 30,
          itemStyle: {
            color: colorPaletteShade.red6,
          },
          data: [
            {
              coord: this.markPointData,
            },
          ],
        },
        areaStyle: {
          color: colorPaletteShade.teal4,
        },
      };
    },
    getTimeValues() {
      return this.tsData.map((query) => query.time);
    },
    toggleColor() {
      const skin = {
        value: this.$store.state.appConfig.layout.skin,
      };
      if (skin.value !== 'dark') return colorPalette.gray;
      return colorPalette.lightGray;
    },
    formatTitle() {
      let scope = '';
      if (this.selectedServer.scope === 'enterprise') {
        scope = 'Enterprise';
      } else if (this.selectedServer.scope === 'internet') {
        scope = 'Internet';
      } else {
        scope = 'All';
      }
      return this.role === 'client' ? `${scope} Client Traffic` : `${scope} Server Traffic`;
    },
    chartOptions() {
      return {
        title: {
          text: this.formatTitle,
          left: 'center',
          textStyle: {
            color: this.toggleColor,
          },
        },
        color: [colorPaletteShade.red6],
        tooltip: {
          trigger: 'axis',
          formatter: (info) => `
              <div class="apache-echarts-tooltip">
                <div class="apache-echarts-tooltip-timestamp mb-n2">
                  Date: ${moment(info[0].data[0]).format(this.timeSeriesFormat)}
                </div>
                <br>
                Bytes: ${readableBytes(info[0].data[1])}
              </div>
            `,
          axisPointer: {
            type: 'line',
            animation: false,
            label: {
              show: false,
            },
          },
        },
        xAxis: {
          type: 'time',
          boundaryGap: false,
          splitLine: {
            show: false,
          },
          axisTick: {
            lineStyle: {
              color: this.toggleColor,
            },
          },
          axisLine: {
            lineStyle: {
              color: this.toggleColor,
            },
          },
          axisLabel: {
            formatter: (value) => moment(value).format(this.timeSeriesFormat),
            fontSize: 9,
          },
        },
        yAxis: {
          type: 'value',
          name: 'Total Bytes',
          nameLocation: 'center',
          nameGap: 50,
          axisTick: {
            lineStyle: {
              color: this.toggleColor,
            },
          },
          axisLine: {
            lineStyle: {
              color: this.toggleColor,
            },
          },
          axisLabel: {
            formatter: (value) => readableBytes(value),
            fontSize: 9,
          },
        },
        series: this.getSeriesValues,
      };
    },
  },
  methods: {
    httpGet() {
      this.loaded = false;
      this.selectedPoint = null;
      this.markPointData = [];
      this.tsData = null;
      this.$http.get(this.httpUrl, this.httpParams)
        .then((response) => { this.tsData = response.data; })
        .finally(() => { this.loaded = true; });
    },
    handleClickEvent(event) {
      this.markPointData = event.value;
      const start = new Date(event.data[0]);
      const end = new Date(event.data[0]);
      end.setMinutes(start.getMinutes() + granularity.granularity24(this.timeRange));
      this.selectedPoint = {
        ip: this.selectedServer.ip,
        start: start.toISOString(),
        end: end.toISOString(),
        role: this.role,
        serviceType: this.serviceType,
        scope: this.selectedServer.scope,
      };
    },
  },
};
</script>
